import { LayoutGrid, Settings, CircleHelp, SquareTerminal, ListChecks, HistoryIcon } from 'lucide-react';
import { Link, useLocation } from 'react-router';

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  useSidebar,
  SidebarMenuItem,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuBadge,
} from '@/components/ui/sidebar';
import {
  IconLogo,
  Logo,
  ImageHome,
  ImageEscrow,
  ImageBackups,
  ImageContinuity,
  ImageSaasEscrow,
  ImageCustodian,
} from 'src/components/Svg/Images';

import { NavUser } from './ui';

const mainItems = [
  {
    title: 'Home',
    url: '/',
    icon: ImageHome,
  },
  {
    title: 'Software Backups',
    url: '/backups',
    icon: ImageBackups,
  },
  {
    title: 'Software Escrows',
    url: '/software-escrows',
    icon: ImageEscrow,
  },
  {
    title: 'SaaS Escrows',
    url: '/saas-escrows',
    icon: ImageSaasEscrow,
  },
  {
    title: 'Continuity Escrows',
    url: '/continuity-escrows',
    icon: ImageContinuity,
  },
  {
    title: 'Software Custodian',
    url: '/custodian-profiles',
    icon: ImageCustodian,
  },
];

const footerItems: {
  icon: React.ElementType;
  label: string;
  to: string;
}[] = [
  {
    icon: CircleHelp,
    label: 'Help and resources',
    to: '/help-resources',
  },
  {
    icon: Settings,
    label: 'Settings',
    to: '/settings',
  },
];

type Props = React.ComponentProps<typeof Sidebar> & {
  user: {
    name: string;
    email: string;
  };
  migrationsCount: number;
  withIntegrations?: boolean;
  withAuditLogs?: boolean;
  tasksCount: number;
};

export function AppSidebar({
  user,
  migrationsCount,
  tasksCount,
  withIntegrations = false,
  withAuditLogs = false,
  ...props
}: Props) {
  const { pathname } = useLocation();
  const { open, isMobile, toggleSidebar } = useSidebar();

  return (
    <Sidebar {...props} collapsible="icon">
      <SidebarHeader>
        <Link to="/" onClick={isMobile ? toggleSidebar : undefined}>
          {open || isMobile ? <Logo className="w-[130px] h-[40px]" /> : <IconLogo className="w-8 h-8 mb-2" />}
        </Link>
      </SidebarHeader>
      <SidebarContent className="py-2 px-2 group-data-[collapsible=icon]:px-0">
        <SidebarMenu className="gap-1 group-data-[collapsible=icon]:gap-0">
          {mainItems.map(({ title, url, icon: Icon }) => (
            <SidebarMenuItem key={title}>
              <SidebarMenuButton
                isActive={isLinkActive(pathname, url)}
                className="p-2! h-12 group-data-[collapsible=icon]:size-12! group-data-[collapsible=icon]:p-3! [&>svg]:size-6"
                asChild
                tooltip={title}
                onClick={isMobile ? toggleSidebar : undefined}
              >
                <Link to={url} className="flex items-center gap-2">
                  <Icon />
                  <span className="text-foreground font-semibold">{title}</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          ))}
        </SidebarMenu>
      </SidebarContent>
      <SidebarFooter>
        <SidebarMenu className="text-foreground data-menu-button:hover:text-foreground">
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              isActive={isLinkActive(pathname, '/tasks')}
              tooltip="Tasks"
              onClick={isMobile ? toggleSidebar : undefined}
            >
              <Link to="/tasks">
                <ListChecks /> Tasks
                {Boolean(tasksCount) && (
                  <SidebarMenuBadge className="bg-foreground text-white group-data-[collapsible=icon]:block group-data-[collapsible=icon]:h-4 group-data-[collapsible=icon]:-translate-y-[13px] group-data-[collapsible=icon]:translate-x-[10px] transition-transform">
                    {tasksCount}
                  </SidebarMenuBadge>
                )}
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
          {withAuditLogs && (
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                isActive={isLinkActive(pathname, '/activity-log')}
                tooltip="Activity log"
                onClick={isMobile ? toggleSidebar : undefined}
              >
                <Link to="/activity-log">
                  <HistoryIcon /> Activity log
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}
          {withIntegrations && (
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                isActive={isLinkActive(pathname, '/integrations')}
                tooltip="Integrations"
                onClick={isMobile ? toggleSidebar : undefined}
              >
                <Link to="/integrations">
                  <LayoutGrid /> Integrations
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}
          {Boolean(migrationsCount) && (
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                isActive={isLinkActive(pathname, '/migrations')}
                tooltip="Migrations"
                onClick={isMobile ? toggleSidebar : undefined}
              >
                <Link to="/migrations">
                  <SquareTerminal /> Migrations
                  <SidebarMenuBadge>{migrationsCount}</SidebarMenuBadge>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}
          {footerItems.map((menu) => (
            <SidebarMenuItem key={menu.label}>
              <SidebarMenuButton
                asChild
                isActive={isLinkActive(pathname, menu.to)}
                tooltip={menu.label}
                onClick={isMobile ? toggleSidebar : undefined}
              >
                <Link to={menu.to}>
                  <menu.icon /> {menu.label}
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          ))}
        </SidebarMenu>
        <NavUser user={user} />
      </SidebarFooter>
    </Sidebar>
  );
}

function isLinkActive(pathname: string, url: string) {
  return pathname === url || (url !== '/' && pathname.startsWith(url + '/'));
}
